import { FileOpener } from '@capacitor-community/file-opener';
import { Directory, Filesystem } from '@capacitor/filesystem';
import write_blob from "capacitor-blob-writer";
import { Http, HttpOptions, HttpResponse, HttpUploadFileOptions } from '@capacitor-community/http';
import { IDownloadFileParams, IGetParams, IHttpResponse, IPostFileParams, IPostParams } from './core-http-client.models';
import { ICoreHttpEngine } from './core-http-engine.interface';
/*
  Generated class for the CoreMobileHttpClientProvider provider.

  See https://angular.io/guide/dependency-injection for more info on providers
  and Angular DI.
*/

export class CoreNativeHttpEngine implements ICoreHttpEngine
{
    // Since the file:// protocol does not support cookies, it was 
    // opted to resort to use the InAppBrowser to execute the requests
    // instead through the HttpClient. Implementation in class below.

    //#region Constructor

    /**
     * Class constructor
     */
    constructor()
    {
    }

    //#endregion


    //#region Methods

    /**
     * Perform a get request through the HTTP plugin and return parse the result in JSON
     * @param {IGetParams} params
     */
    public async get<T>(params: IGetParams): Promise<IHttpResponse<T>>
    {
        // prepare the options object
        const options: HttpOptions = {
            url: params.url,
            responseType: params.responseType,
            webFetchExtra: {
                credentials: 'include'
            }
        };

        // perform the request
        let result = await Http.get(options);

        // Check whether the response was successful
        if (this.isSuccessfulResponse(result))
        {
            // Return the response
            return { status: result.status, data: result.data };
        } else
        {
            // Throw an exception
            throw { status: result.status, data: result.data };
        }
    }

    /**
     * Perform post request through the HTTP plugin
     * @param {IPostParams} params
     */
    public async post<T>(params: IPostParams): Promise<IHttpResponse<T>>
    {
        // prepare the options object
        const options: HttpOptions = {
            url: params.url,
            responseType: params.responseType,
            data: params.body,
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            webFetchExtra: {
                credentials: 'include'
            }
        };

        // perform the request
        let result = await Http.post(options);

        // prepare data placeholder
        let data = null;

        try
        {
            // The native http plugin automatically tries to parse
            // any JSON present, so to counter this we are trying to parse
            // the string as a JSON object by default, and if it fails then
            // return the plain string
            data = JSON.parse(result.data);
        } catch
        {
            data = result.data;
        }

        // Check whether the response was successful
        // Check whether the response was successful
        if (this.isSuccessfulResponse(result))
        {
            // Return the response
            return { status: result.status, data: data };
        } else
        {
            // Throw an exception
            throw { status: result.status, data: data };
        }
    }

    /**
     * Post a file to the server
     * @param {IPostFileParams} params
     */
    public async postFile<T>(params: IPostFileParams): Promise<IHttpResponse<T>>
    {
        // prepare the options object
        const options: HttpUploadFileOptions = {
            url: params.url,
            name: params.name,
            filePath: <string>params.value,
            responseType: params.responseType,
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            webFetchExtra: {
                credentials: 'include'
            }
        };

        // perform the request
        let result = await Http.uploadFile(options);

        let data = null;

        try
        {
            // The native http plugin automatically tries to parse
            // any JSON present, so to counter this we are trying to parse
            // the string as a JSON object by default, and if it fails then
            // return the plain string
            data = JSON.parse(result.data);
        } catch
        {
            data = result.data;
        }

        // Check whether the response was successful
        if (this.isSuccessfulResponse(result))
        {
            // Return the response
            return { status: result.status, data: data };
        } else
        {
            // Throw an exception
            throw { status: result.status, data: data };
        }
    }

    /**
     * Download a file
     * @param params request params
     */
    public async downloadFile(params: IDownloadFileParams): Promise<any>
    {
        // prepare the options object
        const options: HttpOptions = {
            url: params.url,
            responseType: 'blob',
            webFetchExtra: {
                credentials: 'include'
            }
        };

        // Perform the request and retrieve the data property. This will contain a base64 data string without the header.
        // The string then needs to be converted to a blob so that the blob writer can make use of it and write it to disk.
        let result = await Http.get(options);
        if (!this.isSuccessfulResponse(result))
        {
            return Promise.reject({ status: result.status, data: result.data });
        }
        let fileName = this.extractFileName(result);
        let contentType = result.headers[ 'Content-Type' ];

        let byteString = atob(result.data);
        var ab = new ArrayBuffer(byteString.length);
        var ia = new Uint8Array(ab);

        for (var i = 0; i < byteString.length; i++)
        {
            ia[ i ] = byteString.charCodeAt(i);
        }

        // Convert the byte[] to a blob
        let blob = new Blob([ ab ], { type: contentType });

        // Write the blob to disc
        await write_blob({
            directory: Directory.Library,
            blob: blob,
            path: fileName
        });

        // Get the newly created file path
        let fileUri = await Filesystem.getUri({ directory: Directory.Library, path: fileName });

        // Open the file
        await FileOpener.open({ filePath: fileUri.uri });
    }

    /**
     * Clear native layer cookies
     */
    public async clearCookies(): Promise<void>
    {
        await Http.clearAllCookies();
    }

    /**
     * Retrieve the file name from a download file response
     * @param response 
     * @returns 
     */
    private extractFileName(response: HttpResponse)
    {
        return this.getHeaderValue(response, 'Content-Disposition')
            .split('filename=')[ 1 ]
            .split(';')[ 0 ]
            .replace(/\"/g, "");
    }

    /**
     * Get the header value from an HttpResponse
     * @param response HttpResponse object
     * @param key Header key
     * @returns 
     */
    private getHeaderValue(response: HttpResponse, key: string)
    {
        // Convert the key to lowercase so that it can be used for searching
        let lcKey = key.toLowerCase();
        let headerKeys = Object.keys(response.headers);
        for (let i = 0; i < headerKeys.length; i++)
        {
            if (lcKey == headerKeys[ i ].toLowerCase())
            {
                return response.headers[ headerKeys[ i ] ];
            }
        }

        // Key was not found, return null
        return null;
    }

    /**
     * Check whether response received is successful according to the WebAPI documentation
     * https://developer.mozilla.org/en-US/docs/Web/API/Response/ok
     * @param response 
     * @returns 
     */
    private isSuccessfulResponse(response: HttpResponse): boolean
    {
        return response.status >= 200 && response.status < 300
    }

    //#endregion
}